import React, { useState, useEffect } from 'react';
import { Input, InputNumber, DatePicker, TimePicker, Radio, Button, Switch, Checkbox, Typography, Space, Tag, Select, Spin, message } from 'antd';
import { CalendarOutlined, TeamOutlined, CompassOutlined, EnvironmentOutlined, ClockCircleOutlined, GlobalOutlined, CopyOutlined, CarOutlined, PlusOutlined, SwapOutlined, CheckCircleOutlined, LoadingOutlined, SettingOutlined } from '@ant-design/icons';
import { supabase } from '../../../api/supabase';

const { Text } = Typography;
const { Option } = Select;

const PricingPage = () => {
  const [mode, setMode] = useState('charter');
  const [formData, setFormData] = useState({
    date: '',
    numPersons: 1,
    language: 'french',
    showAllYachts: false,
    destination: 'sbh',
    duration: 'half',
    timeSlot: 'morning',
    extraHour: false,
    fullDayPlusSunset: false,
    flightNumber: '',
    departureFlight: '',
    landingFlight: '',
    direction: 'sxm-sbh',
    serviceType: 'vip',
    extraVan: false,
    numExtraVans: 0,
    cruisingHours: 0,
    flightNumberTBC: false,
    landingTimeTBC: false,
  });

  const [copySuccess, setCopySuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [yachtPrices, setYachtPrices] = useState([]);
  const [vipServices, setVipServices] = useState([]);
  const [taxiServices, setTaxiServices] = useState([]);
  const [serviceExtras, setServiceExtras] = useState([]);
  const [seasonConfig, setSeasonConfig] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [peakSeasonStart, setPeakSeasonStart] = useState({ month: 12, day: 23 }); // December 23
  const [peakSeasonEnd, setPeakSeasonEnd] = useState({ month: 1, day: 5 });       // January 5

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: pricesData, error: pricesError } = await supabase.from('yacht_prices').select('*');
        if (pricesError) throw pricesError;

        let enrichedPrices = pricesData || [];
        if (pricesData && pricesData.length > 0) {
          const yachtIds = [...new Set(pricesData.map(p => p.yacht_id).filter(Boolean))];
          if (yachtIds.length > 0) {
            const { data: yachtsData, error: yachtsError } = await supabase.from('yachts').select('*').in('id', yachtIds);
            if (!yachtsError && yachtsData) {
              const yachtMap = new Map(yachtsData.map(y => [y.id, y]));
              enrichedPrices = pricesData.map(price => ({ ...price, yacht: yachtMap.get(price.yacht_id) || null }));
            }
          }
        }

        const { data: vipData } = await supabase.from('vip_services').select('*').order('passengers', { ascending: true });
        const { data: taxiData } = await supabase.from('taxi_services').select('*').order('passengers', { ascending: true });
        const { data: extrasData } = await supabase.from('service_extras').select('*');
        const { data: seasonData } = await supabase.from('season_config').select('*').eq('is_active', true).single();

        setYachtPrices(enrichedPrices);
        setVipServices(vipData || []);
        setTaxiServices(taxiData || []);
        setServiceExtras(extrasData || []);
        setSeasonConfig(seasonData);
      } catch (error) {
        console.error('Error fetching pricing data:', error);
        message.error(`Failed to load pricing data: ${error.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const isPeakSeason = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const month = date.getMonth() + 1; // Convert to 1-indexed (1 = January, 12 = December)
    const day = date.getDate();

    // Peak season spans year boundary (e.g., Dec 23 to Jan 5)
    if (peakSeasonStart.month > peakSeasonEnd.month) {
      // December 23-31 OR January 1-5
      if (month === peakSeasonStart.month && day >= peakSeasonStart.day) return true;
      if (month > peakSeasonStart.month) return true; // After start month in same year
      if (month === peakSeasonEnd.month && day <= peakSeasonEnd.day) return true;
      if (month < peakSeasonEnd.month) return true; // Before end month in new year
    } else {
      // Normal range within same year
      if (month === peakSeasonStart.month && day >= peakSeasonStart.day) return true;
      if (month === peakSeasonEnd.month && day <= peakSeasonEnd.day) return true;
      if (month > peakSeasonStart.month && month < peakSeasonEnd.month) return true;
    }
    return false;
  };

  const getCurrentSeason = () => isPeakSeason(formData.date) ? 'peak' : 'normal';

  useEffect(() => {
    if (mode === 'charter') {
      setFormData(prev => ({ ...prev, destination: 'sbh', duration: 'half', extraHour: false, fullDayPlusSunset: false }));
    }
  }, [mode]);

  useEffect(() => {
    if (formData.destination === 'pinel' || formData.destination === 'anguilla') {
      setFormData(prev => ({ ...prev, duration: 'full', extraHour: false, fullDayPlusSunset: false }));
    }
  }, [formData.destination]);

  useEffect(() => {
    if (formData.duration !== 'half' && formData.duration !== 'full') {
      setFormData(prev => ({ ...prev, extraHour: false, fullDayPlusSunset: false }));
    }
  }, [formData.duration]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const formatProposalDate = (dateString, language) => {
    if (!dateString) return language === 'french' ? 'date non selectionnee' : 'date not selected';
    const dateParts = dateString.split('-');
    const date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
    if (isNaN(date.getTime())) return language === 'french' ? 'date invalide' : 'invalid date';
    const day = date.getDate();

    if (language === 'french') {
      const monthsFr = ['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'];
      return `${day === 1 ? '1er' : day} ${monthsFr[date.getMonth()]} ${date.getFullYear()}`;
    } else {
      const month = date.toLocaleString('en-US', { month: 'long' });
      const year = date.getFullYear();
      let suffix = 'th';
      if (day % 10 === 1 && day !== 11) suffix = 'st';
      if (day % 10 === 2 && day !== 12) suffix = 'nd';
      if (day % 10 === 3 && day !== 13) suffix = 'rd';
      return `${month} the ${day}${suffix} of ${year}`;
    }
  };

  // Charter Functions
  const getFilteredCharterBoats = () => {
    const season = getCurrentSeason();
    const isPeak = isPeakSeason(formData.date);
    const seasonPrices = yachtPrices.filter(yp => yp.season === season);

    let filteredBoats = seasonPrices.map(yp => {
      let price = 0;
      const yacht = yp.yacht;
      const boatLength = yacht?.length_feet || 0;

      if (isPeak && formData.duration === 'half' && formData.destination === 'sbh' && boatLength > 53) return null;

      if (formData.destination === 'sbh') {
        if (formData.duration === 'sunset') price = parseFloat(yp.sunset_sbh) || 0;
        else if (formData.duration === 'half') price = parseFloat(yp.half_day_sbh) || 0;
        else price = parseFloat(yp.full_day_sbh) || 0;
      } else if (formData.destination === 'pinel') {
        price = parseFloat(yp.full_day_sxm) || 0;
      } else if (formData.destination === 'anguilla') {
        price = parseFloat(yp.full_day_axa) || 0;
      }

      if (!price || price === 0) return null;

      return {
        key: yacht?.id,
        name: `${yacht?.brand || ''} ${yacht?.length_feet || ''}ft`.trim(),
        fullName: yacht?.name || '',
        price,
        maxPax: yp.max_passengers || yacht?.max_passengers || 12,
        taxesAXA: parseFloat(yp.axa_taxes) || 650,
        extras: yp.special_inclusions?.join(', ') || null,
        sunsetPrice: parseFloat(yp.sunset_sbh) || 0,
        lengthFeet: boatLength,
        yacht
      };
    }).filter(Boolean);

    if (!formData.showAllYachts) filteredBoats = filteredBoats.filter(boat => boat.maxPax >= formData.numPersons);
    return filteredBoats.sort((a, b) => a.lengthFeet - b.lengthFeet);
  };

  // Get larger boats (>53ft) available at full day price during peak season half day
  const getLargerBoatsForFullDay = () => {
    if (!isPeakSeason(formData.date) || formData.duration !== 'half' || formData.destination !== 'sbh') return [];
    const season = getCurrentSeason();
    const seasonPrices = yachtPrices.filter(yp => yp.season === season);

    let largerBoats = seasonPrices.map(yp => {
      const yacht = yp.yacht;
      const boatLength = yacht?.length_feet || 0;
      if (boatLength <= 53) return null; // Only boats > 53ft

      const fullDayPrice = parseFloat(yp.full_day_sbh) || 0;
      if (!fullDayPrice || fullDayPrice === 0) return null;

      return {
        name: `${yacht?.brand || ''} ${yacht?.length_feet || ''}ft`.trim(),
        price: fullDayPrice,
        maxPax: yp.max_passengers || yacht?.max_passengers || 12,
        lengthFeet: boatLength
      };
    }).filter(Boolean);

    if (!formData.showAllYachts) largerBoats = largerBoats.filter(boat => boat.maxPax >= formData.numPersons);
    return largerBoats.sort((a, b) => a.lengthFeet - b.lengthFeet);
  };

  const calculateCharterPrice = (boat) => {
    let totalPrice = boat.price;
    if (formData.extraHour) {
      if (formData.duration === 'half') totalPrice += Math.round(boat.price / 4);
      else if (formData.duration === 'full') totalPrice += Math.round(boat.price / 8);
    }
    if (formData.fullDayPlusSunset && formData.duration === 'full' && boat.sunsetPrice) totalPrice += boat.sunsetPrice;
    return totalPrice;
  };

  const getTimeSlotText = () => {
    const base = {
      french: formData.duration === 'sunset' ? '17h a 19h' : (formData.duration === 'half' ? (formData.timeSlot === 'morning' ? '9h a 13h' : '13h a 17h') : '9h a 17h'),
      english: formData.duration === 'sunset' ? '5pm to 7pm' : (formData.duration === 'half' ? (formData.timeSlot === 'morning' ? '9am to 1pm' : '1pm to 5pm') : '9am to 5pm')
    };
    if (formData.extraHour) {
      if (formData.duration === 'half') { base.french = formData.timeSlot === 'morning' ? '9h a 14h' : '13h a 18h'; base.english = formData.timeSlot === 'morning' ? '9am to 2pm' : '1pm to 6pm'; }
      else if (formData.duration === 'full') { base.french = '9h a 18h'; base.english = '9am to 6pm'; }
    }
    if (formData.fullDayPlusSunset && formData.duration === 'full') { base.french = '9h a 19h (journee + sunset)'; base.english = '9am to 7pm (full day + sunset)'; }
    return base;
  };

  const getDurationText = () => {
    if (formData.language === 'french') {
      if (formData.duration === 'sunset') return 'sunset';
      if (formData.duration === 'half') return formData.extraHour ? 'demi-journee + 1h' : 'demi-journee';
      if (formData.duration === 'full') return formData.fullDayPlusSunset ? 'journee complete + sunset' : (formData.extraHour ? 'journee complete + 1h' : 'journee complete');
    } else {
      if (formData.duration === 'sunset') return 'sunset';
      if (formData.duration === 'half') return formData.extraHour ? 'half day + 1h' : 'half day';
      if (formData.duration === 'full') return formData.fullDayPlusSunset ? 'full day + sunset' : (formData.extraHour ? 'full day + 1h' : 'full day');
    }
  };

  const generateCharterProposal = () => {
    const filteredBoats = getFilteredCharterBoats();
    const largerBoats = getLargerBoatsForFullDay();
    const timeSlotText = getTimeSlotText();
    const additionalActivities = {
      french: `Nous proposons egalement en supplement :\n- Seabobs : ${formData.duration === 'half' ? '290' : '450'}€ par seabob\n- Jet skis : 140€/heure par jet ski\n- Flyboard : 170€/30min\n- E-surf : 170€/30min\n- Dejeuner a bord du bateau ou drop off a un restaurant de plage`,
      english: `We can also offer you:\n- Seabobs: ${formData.duration === 'half' ? '290' : '450'}€ per seabob\n- Jet skis: 140€/hour per jet ski\n- Flyboard: 170€/30min\n- E-surf: 170€/30min\n- Lunch onboard or drop off at a beach restaurant`
    };

    // Peak season message for larger boats available at full day price
    const largerBoatsMessage = largerBoats.length > 0 ? {
      french: `\n\nDes yachts plus grands sont egalement disponibles au tarif journee complete :\n${largerBoats.map(boat => `- ${boat.name} (MAX ${boat.maxPax} PAX) : ${boat.price}€`).join('\n')}`,
      english: `\n\nLarger yachts are also available at full day rate:\n${largerBoats.map(boat => `- ${boat.name} (MAX ${boat.maxPax} PAX): ${boat.price}€`).join('\n')}`
    } : { french: '', english: '' };

    // Passport requirement only for Anguilla
    const passportRequirement = formData.destination === 'anguilla' ? {
      french: `\n- Les copies des passeports de tous les participants`,
      english: `\n- Copies of all participants' passports`
    } : { french: '', english: '' };

    const proposals = {
      french: `Bonjour,\n\nNous vous remercions pour l'interet que vous portez a l'egard de nos services.\n\nSuite a votre demande, veuillez trouver ci-dessous nos disponibilites pour un ${getDurationText()} autour de ${formData.destination === 'anguilla' ? 'Anguilla' : formData.destination === 'pinel' ? 'Pinel' : 'Saint-Barthelemy'} de ${timeSlotText.french} le ${formatProposalDate(formData.date, 'french')} pour ${formData.numPersons} personne${formData.numPersons > 1 ? 's' : ''} :\n\n${filteredBoats.map(boat => `- ${boat.name} (MAX ${boat.maxPax} PAX) : ${calculateCharterPrice(boat)}€${formData.destination === 'anguilla' ? ` + ${boat.taxesAXA}€ (Taxes AXA)` : ''}${boat.extras ? ` - ${boat.extras}` : ''}`).join('\n')}${largerBoatsMessage.french}\n\nLe prix inclut : serviettes de plage, rose, champagne, bieres, open bar soft drink, equipements de snorkeling, equipage et fuel.\n\n${additionalActivities.french}\n\nPour finaliser votre reservation, merci de nous fournir :${passportRequirement.french}\n- Si des enfants ou bebes participent a l'excursion, merci de nous indiquer leurs ages\n\nNous restons a votre disposition pour toute information complementaire,\n\nBonne journee,`,
      english: `Dear,\n\nThank you for your interest in our services.\n\nFurther to your request, please find below our availabilities for a ${getDurationText()} around ${formData.destination === 'anguilla' ? 'Anguilla' : (formData.destination === 'pinel' ? 'Pinel' : 'St Barth')} from ${timeSlotText.english} on ${formatProposalDate(formData.date, 'english')} for ${formData.numPersons} person${formData.numPersons > 1 ? 's' : ''}:\n\n${filteredBoats.map(boat => `- ${boat.name} (MAX ${boat.maxPax} PAX): ${calculateCharterPrice(boat)}€${formData.destination === 'anguilla' ? ` + ${boat.taxesAXA}€ (Taxes AXA)` : ''}${boat.extras ? ` - ${boat.extras}` : ''}`).join('\n')}${largerBoatsMessage.english}\n\nThe price includes: beach towels, rose, champagne, beers, open bar soft drinks, snorkeling equipment, crew and fuel.\n\n${additionalActivities.english}\n\nTo finalize your reservation, please provide:${passportRequirement.english}\n- If children or babies are part of the excursion, please provide their ages\n\nWe remain at your disposal for any further information.\n\nKind regards,`
    };
    return proposals[formData.language];
  };

  // Transfer Functions
  const getVIPPrice = (persons, isNight) => { const service = vipServices.find(s => s.passengers === persons) || vipServices[vipServices.length - 1]; return service ? (isNight ? parseFloat(service.night_price) : parseFloat(service.day_price)) : 0; };
  const getTaxiPrice = (persons, isNight) => { const effectivePersons = persons <= 2 ? 2 : persons; const service = taxiServices.find(s => s.passengers === effectivePersons) || taxiServices[taxiServices.length - 1]; return service ? (isNight ? parseFloat(service.night_price) : parseFloat(service.day_price)) : 0; };
  const getVanPrice = (isNight, isExtra = false) => { const serviceType = isExtra ? 'vip' : 'taxi'; const extra = serviceExtras.find(e => e.service_type === serviceType && e.extra_name?.includes('Luggage Van')); return extra ? (isNight ? parseFloat(extra.night_price) : parseFloat(extra.day_price)) : (isNight ? 140 : 120); };
  const isNightTime = (timeString) => { if (!timeString) return false; const hour = parseInt(timeString.split(':')[0]); return hour >= 15 || hour < 9; };
  const isEarlyMorningArrival = (timeString) => { if (!timeString) return false; const hour = parseInt(timeString.split(':')[0]); return hour >= 0 && hour < 9; };
  const needsRegularVan = (persons) => persons >= 6 && persons < 7;
  const hasIncludedVan = (persons) => persons >= 7;

  const getFilteredTransferBoats = (isNight = false) => {
    const season = getCurrentSeason();
    const seasonPrices = yachtPrices.filter(yp => yp.season === season);
    let filteredBoats = seasonPrices.map(yp => {
      const yacht = yp.yacht;
      let price = formData.direction === 'axa-sbh' ? parseFloat(yp.full_day_axa) || 0 : (isNight ? parseFloat(yp.transfer_sxm_sbh_night_base) : parseFloat(yp.transfer_sxm_sbh_day_base));
      if (!price || price === 0) return null;
      return { key: yacht?.id, name: `${yacht?.brand || ''} ${yacht?.length_feet || ''}ft`.trim(), fullName: yacht?.name || '', price, maxPax: yp.max_passengers || yacht?.max_passengers || 12, taxesSXM: parseFloat(yp.transfer_sxm_taxes) || 200, taxesAXA: parseFloat(yp.axa_taxes) || 650, halfDayPrice: parseFloat(yp.half_day_sbh) || price, extras: yp.special_inclusions?.join(', ') || null, lengthFeet: yacht?.length_feet || 0 };
    }).filter(Boolean);
    if (!formData.showAllYachts) filteredBoats = filteredBoats.filter(boat => boat.maxPax >= formData.numPersons);
    return filteredBoats.sort((a, b) => a.lengthFeet - b.lengthFeet);
  };

  const calculateCruisingPrice = (boat) => formData.cruisingHours === 0 ? 0 : Math.round((boat.halfDayPrice || boat.price) / 4 * formData.cruisingHours);
  const getBaggageServiceText = (language) => {
    if (hasIncludedVan(formData.numPersons)) { const vanText = formData.extraVan ? (language === 'french' ? ` et ${formData.numExtraVans} van${formData.numExtraVans > 1 ? 's' : ''} supplementaire${formData.numExtraVans > 1 ? 's' : ''}` : ` and ${formData.numExtraVans} additional van${formData.numExtraVans > 1 ? 's' : ''}`) : ''; return language === 'french' ? `\n\nService de transport des bagages inclus (1 van inclus a partir de 7 personnes${vanText}).` : `\n\nLuggage transport service included (1 van included for 7 or more passengers${vanText}).`; }
    else if (formData.serviceType === 'taxi' && (needsRegularVan(formData.numPersons) || formData.extraVan)) { const vanText = []; if (needsRegularVan(formData.numPersons)) vanText.push(language === 'french' ? 'van de bagages' : 'luggage van'); if (formData.extraVan) vanText.push(language === 'french' ? `${formData.numExtraVans} van${formData.numExtraVans > 1 ? 's' : ''} supplementaire${formData.numExtraVans > 1 ? 's' : ''}` : `${formData.numExtraVans} additional van${formData.numExtraVans > 1 ? 's' : ''}`); return language === 'french' ? `\n\nService de transport des bagages inclus avec ${vanText.join(' et ')}.` : `\n\nLuggage transport service included with ${vanText.join(' and ')}.`; }
    return '';
  };

  const generateServicePrice = (isNight = false) => {
    if (formData.serviceType === 'taxi') { const basePrice = getTaxiPrice(formData.numPersons, isNight); const needsVan = needsRegularVan(formData.numPersons); const regularVanPrice = needsVan ? getVanPrice(isNight) : 0; const extraVanPrice = formData.extraVan ? (getVanPrice(isNight, true) * formData.numExtraVans) : 0; let priceString = `${basePrice}€ (Taxi)`; if (needsVan) priceString += ` + ${regularVanPrice}€ (Luggage Van)`; if (extraVanPrice > 0) priceString += ` + ${extraVanPrice}€ (Extra Van${formData.numExtraVans > 1 ? 's' : ''})`; return priceString; }
    return getVIPPrice(formData.numPersons, isNight) + '€ (VIP services)';
  };

  const generateBoatList = (isNight = false) => {
    const arrivalTime = formData.direction === 'sxm-sbh' ? formData.landingFlight : formData.direction === 'sbh-sxm' ? formData.departureFlight : null;
    const isEarlyArrival = (formData.direction === 'sxm-sbh' || formData.direction === 'sbh-sxm') && isEarlyMorningArrival(arrivalTime);
    const filteredBoats = getFilteredTransferBoats(isNight);
    if (formData.direction === 'axa-sbh') return filteredBoats.map(boat => { const cruisingPrice = calculateCruisingPrice(boat); return `${boat.name} (MAX ${boat.maxPax} PAX) : ${boat.price}€ + ${boat.taxesAXA}€ (taxes AXA)${cruisingPrice > 0 ? ` + ${cruisingPrice}€ (${formData.cruisingHours}h cruising)` : ''}`; }).join('\n');
    const servicePrice = generateServicePrice(isNight); const sbhTax = formData.direction === 'sbh-sxm' ? formData.numPersons * 5 : 0; const needsVan = formData.serviceType === 'taxi' && needsRegularVan(formData.numPersons); const regularVanPrice = needsVan ? getVanPrice(isNight) : 0; const extraVanPrice = formData.extraVan ? (getVanPrice(isNight, true) * formData.numExtraVans) : 0; const vanPriceString = [needsVan ? `${regularVanPrice}€ (Luggage Van)` : '', extraVanPrice > 0 ? `${extraVanPrice}€ (Extra Van${formData.numExtraVans > 1 ? 's' : ''})` : ''].filter(Boolean).join(' + ');
    return filteredBoats.map(boat => { let price = boat.price; if (isEarlyArrival) price = Math.round(price * 1.2); const cruisingPrice = calculateCruisingPrice(boat); return `${boat.name} (MAX ${boat.maxPax} PAX) : ${price}€${isEarlyArrival ? ' (Early morning surcharge +20%)' : ''} + ${servicePrice} + ${boat.taxesSXM}€ (taxes SXM)${sbhTax > 0 ? ` + ${sbhTax}€ (taxes SBH)` : ''}${vanPriceString ? ` + ${vanPriceString}` : ''}${cruisingPrice > 0 ? ` + ${cruisingPrice}€ (${formData.cruisingHours}h cruising)` : ''}`; }).join('\n');
  };

  const generateTransferProposal = () => {
    const isNight = formData.direction === 'sxm-sbh' ? isNightTime(formData.landingFlight) : isNightTime(formData.departureFlight);
    const cruisingText = formData.cruisingHours > 0 ? (formData.language === 'french' ? ` avec ${formData.cruisingHours} heure${formData.cruisingHours > 1 ? 's' : ''} de cruising` : ` with ${formData.cruisingHours} hour${formData.cruisingHours > 1 ? 's' : ''} of cruising`) : '';
    const flightNumberText = formData.flightNumberTBC ? (formData.language === 'french' ? 'A CONFIRMER' : 'TO BE CONFIRMED') : formData.flightNumber;
    const landingTimeText = formData.landingTimeTBC ? (formData.language === 'french' ? 'A CONFIRMER' : 'TBC') : formData.landingFlight;
    const departureTimeText = formData.landingTimeTBC ? (formData.language === 'french' ? 'A CONFIRMER' : 'TBC') : formData.departureFlight;

    const boatsText = {
      french: { 'sxm-sbh': `Suite a votre demande, voici nos tarifs et disponibilites pour un transfert de St Martin a St Barth${cruisingText}:\n\nLe ${formatProposalDate(formData.date, 'french')} pour ${formData.numPersons} personne${formData.numPersons > 1 ? 's' : ''} sur le vol ${flightNumberText}, atterrissage a ${landingTimeText}:\n\n${generateBoatList(isNight)}`, 'sbh-sxm': `Suite a votre demande, voici nos tarifs et disponibilites pour un transfert de St Barth a St Martin${cruisingText}:\n\nLe ${formatProposalDate(formData.date, 'french')} pour ${formData.numPersons} personne${formData.numPersons > 1 ? 's' : ''} sur le vol ${flightNumberText}, depart de l'agence Masterski-Pilou, 14 Rue Jeanne d'Arc, Gustavia a ${departureTimeText} (3 heures avant le decollage), heure du vol a ${landingTimeText}:\n\n${generateBoatList(isNight)}`, 'axa-sbh': `Suite a votre demande, voici nos tarifs et disponibilites pour un transfert d'Anguilla a St Barth${cruisingText}:\n\nLe ${formatProposalDate(formData.date, 'french')} pour ${formData.numPersons} personne${formData.numPersons > 1 ? 's' : ''} sur le vol ${flightNumberText}, atterrissage a TQPF a ${landingTimeText}:\n\n${generateBoatList()}` },
      english: { 'sxm-sbh': `Further to your request, please find below our rates and availability for a transfer from St Martin to St Barth${cruisingText}:\n\nOn ${formatProposalDate(formData.date, 'english')} for ${formData.numPersons} person${formData.numPersons > 1 ? 's' : ''} on flight #${flightNumberText}, landing at ${landingTimeText}:\n\n${generateBoatList(isNight)}`, 'sbh-sxm': `Further to your request, please find below our rates and availability for a transfer from St Barth to St Martin${cruisingText}:\n\nOn ${formatProposalDate(formData.date, 'english')} for ${formData.numPersons} person${formData.numPersons > 1 ? 's' : ''} on flight #${flightNumberText}, departure from Masterski-Pilou agency, 14 Rue Jeanne d'Arc, Gustavia at ${departureTimeText} (3 hours before takeoff), flight time at ${landingTimeText}:\n\n${generateBoatList(isNight)}`, 'axa-sbh': `Further to your request, please find below our rates and availability for the transfer from Anguilla to St Barth${cruisingText}:\n\nOn ${formatProposalDate(formData.date, 'english')} for ${formData.numPersons} person${formData.numPersons > 1 ? 's' : ''} on flight #${flightNumberText}, landing at TQPF at ${landingTimeText}:\n\n${generateBoatList()}` }
    };

    const proposals = {
      french: { 'sxm-sbh': `Bonjour,\n\nNous vous remercions pour l'interet que vous portez a nos services.\n\n${boatsText.french['sxm-sbh']}\n\nLe prix inclut : serviettes de plage, open bar (soft, rose, champagne, bieres), equipements de snorkeling, equipage, et carburant.${getBaggageServiceText('french')}\n\nService VIP : notre agent accueillera vos clients a la sortie de l'avion, facilitera leur passage a l'immigration, s'occupera de leurs bagages, et les accompagnera directement jusqu'au quai pour embarquer sur le bateau. Ce service est egalement disponible pour le retour de St Barth a St Martin.\n\nLe trajet en bateau dure environ 1h15 (selon les conditions meteorologiques)${formData.cruisingHours > 0 ? ` plus ${formData.cruisingHours}h de cruising autour des iles` : ''}.\n\nPour finaliser toute reservation, merci de fournir :\n- Les copies des passeports de tous les voyageurs\n- Les informations sur le vol (numero de vol)\n- La destination finale a St Barth\n- Si des enfants ou bebes participent au transfert, merci de nous indiquer leurs ages\n\nNous esperons que ces informations repondent a votre demande et restons a votre disposition pour toute question supplementaire.\n\nBelle journee,`, 'sbh-sxm': `Bonjour,\n\nNous vous remercions pour l'interet que vous portez a nos services.\n\n${boatsText.french['sbh-sxm']}\n\nLe prix inclut : serviettes de plage, open bar (soft, rose, champagne, bieres), equipements de snorkeling, equipage, et carburant.${getBaggageServiceText('french')}\n\nService VIP : notre agent accueillera vos clients a la sortie de l'avion, facilitera leur passage a l'immigration, s'occupera de leurs bagages, et les accompagnera directement jusqu'au quai pour embarquer sur le bateau. Ce service est egalement disponible pour le retour de St Barth a St Martin.\n\nLe trajet en bateau dure environ 1h15 (selon les conditions meteorologiques)${formData.cruisingHours > 0 ? ` plus ${formData.cruisingHours}h de cruising autour des iles` : ''}.\n\nPour finaliser toute reservation, merci de fournir :\n- Les copies des passeports de tous les voyageurs\n- Les informations sur le vol (numero de vol)\n- La destination finale a St Martin\n- Si des enfants ou bebes participent au transfert, merci de nous indiquer leurs ages\n\nNous esperons que ces informations repondent a votre demande et restons a votre disposition pour toute question supplementaire.\n\nBelle journee,`, 'axa-sbh': `Bonjour,\n\nNous vous remercions pour l'interet que vous portez a nos services.\n\n${boatsText.french['axa-sbh']}\n\nLe prix inclut : carburant, equipage, serviettes, boissons soft, rose, champagne, et transport vers Anguilla.${getBaggageServiceText('french')}\n\nLe trajet en bateau dure environ 2h30 (selon les conditions meteorologiques)${formData.cruisingHours > 0 ? ` plus ${formData.cruisingHours}h de cruising autour des iles` : ''}.\n\nOBLIGATOIRE POUR FINALISER LA RESERVATION :\n- Copies des passeports de tous les voyageurs (72 heures a l'avance)\n- Informations sur le vol (numero de vol)\n- Destination finale a St Barth\n- Si des enfants ou bebes participent au transfert, merci de nous indiquer leurs ages\n\nNous esperons que ces informations repondent a votre demande et restons a votre disposition pour toute question supplementaire.\n\nBelle journee,` },
      english: { 'sxm-sbh': `Dear,\n\nThank you for your interest in our services.\n\n${boatsText.english['sxm-sbh']}\n\nThe price includes: beach towels, open bar (soft drinks, rose, champagne, beers), snorkeling equipment, crew, and fuel.${getBaggageServiceText('english')}\n\nVIP SERVICES: Our agent will greet customers as they exit the aircraft, assist them with a faster passage through immigration, collect and carry their luggage, and escort them to the dock for direct boarding. This service is also available for the return journey from St. Barth to St. Martin.\n\nThe journey on board takes approximately 1 hour and 15 minutes (subject to weather conditions)${formData.cruisingHours > 0 ? ` plus ${formData.cruisingHours}h of cruising around the islands` : ''}.\n\nTo finalize your reservation, please provide:\n- Copies of all passengers' passports\n- Flight details (flight number)\n- Final destination at St. Barth\n- If children or babies are part of the transfer, please provide their ages\n\nWe hope this information meets your needs, and we remain at your disposal for any further assistance.\n\nKind regards,`, 'sbh-sxm': `Dear,\n\nThank you for your interest in our services.\n\n${boatsText.english['sbh-sxm']}\n\nThe price includes: beach towels, open bar (soft drinks, rose, champagne, beers), snorkeling equipment, crew, and fuel.${getBaggageServiceText('english')}\n\nVIP SERVICES: Our agent will greet customers as they exit the aircraft, assist them with a faster passage through immigration, collect and carry their luggage, and escort them to the dock for direct boarding. This service is also available for the return journey from St. Barth to St. Martin.\n\nThe journey on board takes approximately 1 hour and 15 minutes (subject to weather conditions)${formData.cruisingHours > 0 ? ` plus ${formData.cruisingHours}h of cruising around the islands` : ''}.\n\nTo finalize your reservation, please provide:\n- Copies of all passengers' passports\n- Flight details (flight number)\n- Final destination at St. Martin\n- If children or babies are part of the transfer, please provide their ages\n\nWe hope this information meets your needs, and we remain at your disposal for any further assistance.\n\nKind regards,`, 'axa-sbh': `Dear,\n\nThank you for your interest in our services.\n\n${boatsText.english['axa-sbh']}\n\nPrice includes: fuel, skippers, towels, soft drinks, rose, champagne, and transport to Anguilla.${getBaggageServiceText('english')}\n\nThe trip on board takes approximately 2h30 (depending on weather conditions)${formData.cruisingHours > 0 ? ` plus ${formData.cruisingHours}h of cruising around the islands` : ''}.\n\nMANDATORY TO FINALIZE ALL BOOKINGS:\n- Copies of all passengers' passports (72 Hours in advance)\n- Flight details (flight number)\n- Final destination at St. Barth\n- If children or babies are part of the transfer, please provide their ages\n\nWe hope to have answered your request and remain at your disposal for any further information.\n\nKind regards,` }
    };
    return proposals[formData.language][formData.direction];
  };

  const generateProposal = () => mode === 'charter' ? generateCharterProposal() : generateTransferProposal();
  const handleCopy = () => { navigator.clipboard.writeText(generateProposal()); setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); };
  const isFormValid = () => mode === 'charter' ? formData.date && formData.numPersons : formData.date && formData.numPersons && (formData.flightNumber || formData.flightNumberTBC);
  const getAvailableBoatsCount = () => mode === 'charter' ? getFilteredCharterBoats().length : getFilteredTransferBoats(isNightTime(formData.direction === 'sxm-sbh' ? formData.landingFlight : formData.departureFlight)).length;

  // Styles
  const styles = {
    page: { minHeight: '100vh', background: '#fafafa', padding: '32px' },
    container: { maxWidth: '1440px', margin: '0 auto' },
    header: { marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #e5e5e5' },
    headerContent: { display: 'flex', alignItems: 'center', gap: '20px' },
    headerIcon: { width: '56px', height: '56px', background: '#000', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px' },
    headerTitle: { margin: 0, fontSize: '28px', fontWeight: 700, color: '#000', letterSpacing: '-0.5px' },
    headerSubtitle: { margin: 0, fontSize: '14px', color: '#666', marginTop: '4px' },
    grid: { display: 'grid', gridTemplateColumns: '480px 1fr', gap: '32px' },
    card: { background: '#fff', borderRadius: '20px', border: '1px solid #e5e5e5', padding: '28px' },
    sectionLabel: { fontSize: '11px', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' },
    fieldLabel: { fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' },
    input: { height: '48px', borderRadius: '12px', border: '1px solid #e0e0e0', fontSize: '14px', width: '100%' },
    segmentBtn: (active) => ({ flex: 1, height: '48px', borderRadius: '12px', border: active ? 'none' : '1px solid #e0e0e0', background: active ? '#000' : '#fff', color: active ? '#fff' : '#333', fontWeight: 600, fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }),
    segmentBtnSm: (active) => ({ flex: 1, height: '44px', borderRadius: '10px', border: active ? 'none' : '1px solid #e0e0e0', background: active ? '#000' : '#fff', color: active ? '#fff' : '#333', fontWeight: 600, fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }),
    divider: { height: '1px', background: '#eee', margin: '24px 0' },
    optionsBox: { background: '#f8f8f8', borderRadius: '14px', padding: '20px', border: '1px solid #eee' },
    statsBox: { background: '#000', borderRadius: '16px', padding: '20px', marginBottom: '20px' },
    statsNumber: { fontSize: '32px', fontWeight: 700, color: '#fff' },
    statsLabel: { fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' },
    primaryBtn: { height: '56px', borderRadius: '14px', background: '#000', border: 'none', color: '#fff', fontSize: '15px', fontWeight: 700, cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s' },
    tag: (color) => ({ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: color === 'dark' ? 'rgba(0,0,0,0.08)' : color === 'orange' ? 'rgba(234,88,12,0.1)' : 'rgba(0,0,0,0.05)', color: color === 'dark' ? '#333' : color === 'orange' ? '#ea580c' : '#666' }),
    previewEmpty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '450px', textAlign: 'center' },
    previewEmptyIcon: { width: '72px', height: '72px', background: '#f5f5f5', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', marginBottom: '20px' },
    previewContent: { background: '#f8f8f8', borderRadius: '14px', padding: '24px', minHeight: '450px' },
    previewText: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontSize: '13px', lineHeight: 2, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', color: '#333' },
  };

  if (loading) {
    return (
      <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 40, color: '#000' }} spin />} />
          <p style={{ marginTop: '16px', color: '#666', fontWeight: 500 }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={styles.headerContent}>
              <div style={styles.headerIcon}>{mode === 'charter' ? '🛥️' : '✈️'}</div>
              <div>
                <h1 style={styles.headerTitle}>{mode === 'charter' ? 'Charter' : 'Transfer'} Quote</h1>
                <p style={styles.headerSubtitle}>
                  Generate professional quotes instantly
                  {isPeakSeason(formData.date) && formData.date && <span style={{ ...styles.tag('orange'), marginLeft: '12px' }}>Peak Season</span>}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowSettings(!showSettings)}
              style={{
                width: '44px', height: '44px', borderRadius: '12px',
                border: showSettings ? 'none' : '1px solid #e0e0e0',
                background: showSettings ? '#000' : '#fff',
                color: showSettings ? '#fff' : '#666',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s'
              }}
            >
              <SettingOutlined style={{ fontSize: '18px' }} />
            </button>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div style={{ marginTop: '20px', padding: '20px', background: '#f8f8f8', borderRadius: '14px', border: '1px solid #eee' }}>
              <div style={{ ...styles.sectionLabel, marginBottom: '16px' }}><SettingOutlined /> Peak Season Settings</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <div style={styles.fieldLabel}>Start Date</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Select
                      value={peakSeasonStart.month}
                      onChange={(v) => setPeakSeasonStart(prev => ({ ...prev, month: v }))}
                      style={{ flex: 1 }}
                    >
                      <Option value={1}>January</Option>
                      <Option value={2}>February</Option>
                      <Option value={3}>March</Option>
                      <Option value={4}>April</Option>
                      <Option value={5}>May</Option>
                      <Option value={6}>June</Option>
                      <Option value={7}>July</Option>
                      <Option value={8}>August</Option>
                      <Option value={9}>September</Option>
                      <Option value={10}>October</Option>
                      <Option value={11}>November</Option>
                      <Option value={12}>December</Option>
                    </Select>
                    <InputNumber
                      min={1} max={31}
                      value={peakSeasonStart.day}
                      onChange={(v) => setPeakSeasonStart(prev => ({ ...prev, day: v }))}
                      style={{ width: '70px' }}
                    />
                  </div>
                </div>
                <div>
                  <div style={styles.fieldLabel}>End Date</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Select
                      value={peakSeasonEnd.month}
                      onChange={(v) => setPeakSeasonEnd(prev => ({ ...prev, month: v }))}
                      style={{ flex: 1 }}
                    >
                      <Option value={1}>January</Option>
                      <Option value={2}>February</Option>
                      <Option value={3}>March</Option>
                      <Option value={4}>April</Option>
                      <Option value={5}>May</Option>
                      <Option value={6}>June</Option>
                      <Option value={7}>July</Option>
                      <Option value={8}>August</Option>
                      <Option value={9}>September</Option>
                      <Option value={10}>October</Option>
                      <Option value={11}>November</Option>
                      <Option value={12}>December</Option>
                    </Select>
                    <InputNumber
                      min={1} max={31}
                      value={peakSeasonEnd.day}
                      onChange={(v) => setPeakSeasonEnd(prev => ({ ...prev, day: v }))}
                      style={{ width: '70px' }}
                    />
                  </div>
                </div>
              </div>
              <p style={{ margin: '12px 0 0', fontSize: '12px', color: '#888' }}>
                Peak season: {['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][peakSeasonStart.month]} {peakSeasonStart.day} → {['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][peakSeasonEnd.month]} {peakSeasonEnd.day} (boats &gt;53ft only available at full day rate for half-day charters)
              </p>
            </div>
          )}
        </div>

        <div style={styles.grid}>
          {/* Left Panel */}
          <div style={styles.card}>
            {/* Service Type */}
            <div style={{ marginBottom: '28px' }}>
              <div style={styles.sectionLabel}><SwapOutlined /> Service Type</div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => setMode('charter')} style={styles.segmentBtn(mode === 'charter')}>🛥️ Charter</button>
                <button onClick={() => setMode('transfer')} style={styles.segmentBtn(mode === 'transfer')}>✈️ Transfer</button>
              </div>
            </div>

            <div style={styles.divider} />

            {/* Essential Details */}
            <div style={{ marginBottom: '28px' }}>
              <div style={styles.sectionLabel}><CalendarOutlined /> Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <div style={styles.fieldLabel}><CalendarOutlined /> Date <span style={{ color: '#e53935' }}>*</span></div>
                  <DatePicker placeholder="Select" onChange={(date) => { const d = date ? date.add(1, 'day') : null; handleChange('date', d ? d.format('YYYY-MM-DD') : ''); }} style={styles.input} />
                </div>
                <div>
                  <div style={styles.fieldLabel}><TeamOutlined /> Guests <span style={{ color: '#e53935' }}>*</span></div>
                  <InputNumber min={1} max={16} value={formData.numPersons} onChange={(v) => handleChange('numPersons', v)} style={styles.input} />
                </div>
              </div>
              {mode === 'transfer' && (
                <div>
                  <div style={styles.fieldLabel}>✈️ Flight <span style={{ color: '#e53935' }}>*</span></div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Input placeholder={formData.flightNumberTBC ? 'TBC' : 'Ex: AF498'} value={formData.flightNumberTBC ? '' : formData.flightNumber} onChange={(e) => handleChange('flightNumber', e.target.value)} disabled={formData.flightNumberTBC} style={{ ...styles.input, flex: 1 }} />
                    <button onClick={() => { handleChange('flightNumberTBC', !formData.flightNumberTBC); if (!formData.flightNumberTBC) handleChange('flightNumber', ''); }} style={{ ...styles.segmentBtnSm(formData.flightNumberTBC), width: '64px', flex: 'none' }}>TBC</button>
                  </div>
                </div>
              )}
            </div>

            <div style={styles.divider} />

            {/* Charter Options */}
            {mode === 'charter' && (
              <>
                <div style={{ marginBottom: '28px' }}>
                  <div style={styles.sectionLabel}><EnvironmentOutlined /> Destination</div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {[{ k: 'sbh', l: 'St Barth' }, { k: 'pinel', l: 'Pinel' }, { k: 'anguilla', l: 'Anguilla' }].map(d => (
                      <button key={d.k} onClick={() => handleChange('destination', d.k)} style={styles.segmentBtnSm(formData.destination === d.k)}>{d.l}</button>
                    ))}
                  </div>
                </div>

                {formData.destination === 'sbh' && (
                  <div style={{ marginBottom: '28px' }}>
                    <div style={styles.sectionLabel}><ClockCircleOutlined /> Duration</div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {[{ k: 'half', l: 'Half Day' }, { k: 'full', l: 'Full Day' }, { k: 'sunset', l: 'Sunset' }].map(d => (
                        <button key={d.k} onClick={() => handleChange('duration', d.k)} style={styles.segmentBtnSm(formData.duration === d.k)}>{d.l}</button>
                      ))}
                    </div>
                  </div>
                )}

                {formData.destination === 'sbh' && (formData.duration === 'half' || formData.duration === 'full') && (
                  <div style={{ ...styles.optionsBox, marginBottom: '28px' }}>
                    <div style={styles.sectionLabel}><PlusOutlined /> Extensions</div>
                    {formData.duration === 'half' && <Checkbox checked={formData.extraHour} onChange={(e) => handleChange('extraHour', e.target.checked)} style={{ fontWeight: 500 }}>+1 Hour <span style={styles.tag('orange')}>+25%</span></Checkbox>}
                    {formData.duration === 'full' && (
                      <Space direction="vertical" size="small">
                        <Checkbox checked={formData.extraHour} disabled={formData.fullDayPlusSunset} onChange={(e) => handleChange('extraHour', e.target.checked)} style={{ fontWeight: 500 }}>+1 Hour <span style={styles.tag('orange')}>+12.5%</span></Checkbox>
                        <Checkbox checked={formData.fullDayPlusSunset} disabled={formData.extraHour} onChange={(e) => { handleChange('fullDayPlusSunset', e.target.checked); if (e.target.checked) handleChange('extraHour', false); }} style={{ fontWeight: 500 }}>+ Sunset <span style={styles.tag('dark')}>5-7pm</span></Checkbox>
                      </Space>
                    )}
                  </div>
                )}

                {formData.destination === 'sbh' && formData.duration === 'half' && (
                  <div style={{ marginBottom: '28px' }}>
                    <div style={styles.sectionLabel}>Time Slot</div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => handleChange('timeSlot', 'morning')} style={styles.segmentBtnSm(formData.timeSlot === 'morning')}>Morning {formData.extraHour ? '9-2pm' : '9-1pm'}</button>
                      <button onClick={() => handleChange('timeSlot', 'afternoon')} style={styles.segmentBtnSm(formData.timeSlot === 'afternoon')}>Afternoon {formData.extraHour ? '1-6pm' : '1-5pm'}</button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Transfer Options */}
            {mode === 'transfer' && (
              <>
                <div style={{ marginBottom: '28px' }}>
                  <div style={styles.sectionLabel}><CompassOutlined /> Direction</div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {[{ k: 'sxm-sbh', l: 'SXM → SBH' }, { k: 'sbh-sxm', l: 'SBH → SXM' }, { k: 'axa-sbh', l: 'AXA → SBH' }].map(d => (
                      <button key={d.k} onClick={() => handleChange('direction', d.k)} style={styles.segmentBtnSm(formData.direction === d.k)}>{d.l}</button>
                    ))}
                  </div>
                </div>

                {formData.direction === 'sbh-sxm' ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '28px' }}>
                    <div>
                      <div style={styles.fieldLabel}><ClockCircleOutlined /> Departure</div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <TimePicker format="HH:mm" placeholder={formData.landingTimeTBC ? 'TBC' : 'Time'} onChange={(t, ts) => handleChange('departureFlight', ts)} disabled={formData.landingTimeTBC} style={{ ...styles.input, flex: 1 }} />
                        <button onClick={() => { handleChange('landingTimeTBC', !formData.landingTimeTBC); if (!formData.landingTimeTBC) { handleChange('departureFlight', ''); handleChange('landingFlight', ''); } }} style={{ ...styles.segmentBtnSm(formData.landingTimeTBC), width: '56px', flex: 'none' }}>TBC</button>
                      </div>
                    </div>
                    <div>
                      <div style={styles.fieldLabel}><ClockCircleOutlined /> Flight</div>
                      <TimePicker format="HH:mm" placeholder={formData.landingTimeTBC ? 'TBC' : 'Takeoff'} onChange={(t, ts) => handleChange('landingFlight', ts)} disabled={formData.landingTimeTBC} style={styles.input} />
                    </div>
                  </div>
                ) : (
                  <div style={{ marginBottom: '28px' }}>
                    <div style={styles.fieldLabel}><ClockCircleOutlined /> {formData.direction === 'axa-sbh' ? 'Landing TQPF' : 'Landing'}</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <TimePicker format="HH:mm" placeholder={formData.landingTimeTBC ? 'TBC' : 'Time'} onChange={(t, ts) => handleChange('landingFlight', ts)} disabled={formData.landingTimeTBC} style={{ ...styles.input, flex: 1 }} />
                      <button onClick={() => { handleChange('landingTimeTBC', !formData.landingTimeTBC); if (!formData.landingTimeTBC) handleChange('landingFlight', ''); }} style={{ ...styles.segmentBtnSm(formData.landingTimeTBC), width: '56px', flex: 'none' }}>TBC</button>
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: '28px' }}>
                  <div style={styles.sectionLabel}><CarOutlined /> Service</div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => handleChange('serviceType', 'vip')} style={styles.segmentBtnSm(formData.serviceType === 'vip')}>VIP</button>
                    <button onClick={() => handleChange('serviceType', 'taxi')} style={styles.segmentBtnSm(formData.serviceType === 'taxi')}>Taxi</button>
                  </div>
                </div>

                <div style={{ ...styles.optionsBox, marginBottom: '28px' }}>
                  <div style={styles.sectionLabel}><PlusOutlined /> Options</div>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={styles.fieldLabel}>Cruising</div>
                    <Select style={{ width: '100%' }} value={formData.cruisingHours} onChange={(v) => handleChange('cruisingHours', v)}>
                      <Option value={0}>No cruising</Option>
                      <Option value={1}>1 hour</Option>
                      <Option value={2}>2 hours</Option>
                      <Option value={3}>3 hours</Option>
                      <Option value={4}>4 hours</Option>
                    </Select>
                  </div>
                  {formData.direction !== 'axa-sbh' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                      <div>
                        <div style={styles.fieldLabel}>Extra Van</div>
                        <Switch checked={formData.extraVan} onChange={(c) => handleChange('extraVan', c)} />
                      </div>
                      {formData.extraVan && <div style={{ flex: 1 }}><div style={styles.fieldLabel}>Qty</div><InputNumber min={1} max={5} value={formData.numExtraVans} onChange={(v) => handleChange('numExtraVans', v)} style={{ width: '100%' }} /></div>}
                    </div>
                  )}
                </div>
              </>
            )}

            <div style={styles.divider} />

            {/* Language */}
            <div style={{ marginBottom: '28px' }}>
              <div style={styles.sectionLabel}><GlobalOutlined /> Language</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => handleChange('language', 'french')} style={styles.segmentBtnSm(formData.language === 'french')}>Francais</button>
                <button onClick={() => handleChange('language', 'english')} style={styles.segmentBtnSm(formData.language === 'english')}>English</button>
              </div>
            </div>

            <Checkbox checked={formData.showAllYachts} onChange={(e) => handleChange('showAllYachts', e.target.checked)} style={{ marginBottom: '24px', fontWeight: 500, color: '#666' }}>Show all yachts</Checkbox>

            {/* Stats */}
            {isFormValid() && (
              <div style={styles.statsBox}>
                <div style={styles.statsNumber}>{getAvailableBoatsCount()}</div>
                <div style={styles.statsLabel}>yacht{getAvailableBoatsCount() !== 1 ? 's' : ''} available</div>
                {(isPeakSeason(formData.date) || (mode === 'charter' && (formData.extraHour || formData.fullDayPlusSunset)) || (mode === 'transfer' && formData.cruisingHours > 0)) && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                    {mode === 'charter' && formData.extraHour && <span style={{ ...styles.tag('dark'), background: 'rgba(255,255,255,0.15)', color: '#fff' }}>+1H</span>}
                    {mode === 'charter' && formData.fullDayPlusSunset && <span style={{ ...styles.tag('dark'), background: 'rgba(255,255,255,0.15)', color: '#fff' }}>Sunset</span>}
                    {mode === 'transfer' && formData.cruisingHours > 0 && <span style={{ ...styles.tag('dark'), background: 'rgba(255,255,255,0.15)', color: '#fff' }}>{formData.cruisingHours}h Cruising</span>}
                    {isPeakSeason(formData.date) && <span style={{ ...styles.tag('dark'), background: 'rgba(234,88,12,0.3)', color: '#fff' }}>Peak</span>}
                  </div>
                )}
              </div>
            )}

            {/* Copy Button */}
            <button onClick={handleCopy} disabled={!isFormValid()} style={{ ...styles.primaryBtn, opacity: isFormValid() ? 1 : 0.4 }}>
              {copySuccess ? <><CheckCircleOutlined /> Copied!</> : <><CopyOutlined /> Copy Quote</>}
            </button>
          </div>

          {/* Right Panel */}
          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <div style={{ width: '48px', height: '48px', background: '#000', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>📋</div>
              <div>
                <Text style={{ fontSize: '18px', fontWeight: 700, color: '#000' }}>Preview</Text>
                {isFormValid() && <span style={{ ...styles.tag('dark'), marginLeft: '12px', background: 'rgba(34,197,94,0.1)', color: '#16a34a' }}><CheckCircleOutlined style={{ marginRight: '4px' }} />Ready</span>}
              </div>
            </div>

            {!isFormValid() ? (
              <div style={styles.previewEmpty}>
                <div style={styles.previewEmptyIcon}>📝</div>
                <Text style={{ fontSize: '16px', fontWeight: 600, color: '#333', marginBottom: '8px' }}>Fill in the form</Text>
                <Text style={{ fontSize: '13px', color: '#999' }}>{mode === 'charter' ? 'Date and guests required' : 'Date, guests, and flight required'}</Text>
              </div>
            ) : (
              <div style={styles.previewContent}>
                <pre style={styles.previewText}>{generateProposal()}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
